import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Tab, Tabs, Button, Modal, Form, Alert, Badge } from 'react-bootstrap';
import { ToastContainer, toast } from 'react-toastify';
import { FaEdit, FaTrash, FaSave, FaTimes, FaSearch } from 'react-icons/fa';
import 'react-toastify/dist/ReactToastify.css';
import '../Customize_Css/Settings.css';
import '../Customize_Css/ViewVendor.css';
import '../Customize_Css/CancellationModal.css';
import Sidebar from '../Components/Siderbar';
import Footer from '../Components/Footer';

const Settings = () => {
    const epicxTenantId = localStorage.getItem('epicxTenantId');
    const BASE_URL = process.env.REACT_APP_BASE_URL;
    const AuthorizationToken = localStorage.getItem('token');
    const [isActive, setIsActive] = useState(false);
    const [timeSettings, setTimeSettings] = useState({
        productVendorOrderAcceptTime: { value: '', unit: 'minutes' },
        cutoffTime: { value: '', unit: 'minutes' },
        cartRemoveTime: { value: '', unit: 'minutes' },
        deliveryStartTime: '',
        deliveryEndTime: '',
        reassignmentTimeout: { value: '', unit: 'seconds' },
        recentorder: { value: '', unit: 'minutes' }
    });

    const [cancellationReasons, setCancellationReasons] = useState([]);
    const [newReason, setNewReason] = useState('');
    const [showModal, setShowModal] = useState(false);
    const [editReasonId, setEditReasonId] = useState(null);
    const [editedReasonText, setEditedReasonText] = useState('');
    const [editedActiveStatus, setEditedActiveStatus] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        axios.get(`${BASE_URL}/login/api/additionalSettings/${epicxTenantId}`, {
            headers: { Authorization: `Bearer ${AuthorizationToken}` }
        })
            .then(response => {
                if (response.data.success && response.data.data) {
                    setTimeSettings({
                        productVendorOrderAcceptTime: response.data.data.productVendorOrderAcceptTime || { value: '', unit: 'minutes' },
                        cutoffTime: response.data.data.cutoffTime || { value: '', unit: 'minutes' },
                        cartRemoveTime: response.data.data.cartRemoveTime || { value: '', unit: 'minutes' },
                        deliveryStartTime: response.data.data.deliveryStartTime || '',
                        deliveryEndTime: response.data.data.deliveryEndTime || '',
                        reassignmentTimeout: response.data.data.reassignmentTimeout || { value: '', unit: 'seconds' },
                        recentorder: response.data.data.recentorder || { value: '', unit: 'minutes' }
                    });
                    setIsActive(response.data.data.isActive || false);
                }
            })
            .catch(error => console.error("Error fetching settings:", error));

        axios.get(`${BASE_URL}/orders/api/cancellations/${epicxTenantId}`, {
            headers: { Authorization: `Bearer ${AuthorizationToken}` }
        })
            .then(res => setCancellationReasons(res.data))
            .catch(err => console.error("Error fetching cancellation reasons:", err));
    }, [BASE_URL, epicxTenantId, AuthorizationToken]);

    const handleTimeChange = (field, type, value) => {
        if (type === 'value' && (isNaN(value) || value < 0)) return;
        setTimeSettings(prev => ({
            ...prev,
            [field]: { ...prev[field], [type]: value }
        }));
    };

    const handleCancel = () => {
        setTimeSettings({
            productVendorOrderAcceptTime: { value: '', unit: 'minutes' },
            cutoffTime: { value: '', unit: 'minutes' },
            cartRemoveTime: { value: '', unit: 'minutes' },
            deliveryStartTime: '',
            deliveryEndTime: '',
            reassignmentTimeout: { value: '', unit: 'seconds' },
            recentorder: { value: '', unit: 'minutes' }
        });
        setIsActive(false);
        setNewReason('');
    };

    const handleSave = () => {
        const payload = {
            ...timeSettings,
            isActive
        };

        axios.post(`${BASE_URL}/login/api/additionalSettings/${epicxTenantId}`, payload, {
            headers: { Authorization: `Bearer ${AuthorizationToken}` }
        })
            .then(() => {
                toast.success("Settings saved successfully!", {
                    position: "top-center",
                    autoClose: 3000
                });
                handleCancel();
            })
            .catch(error => {
                console.error("Error saving settings:", error);
                toast.error("Failed to save settings");
            });
    };

    const handleAddReason = () => {
        if (!newReason.trim()) return;

        axios.post(`${BASE_URL}/orders/api/cancellations/${epicxTenantId}`, { reason: newReason, active: true }, {
            headers: { Authorization: `Bearer ${AuthorizationToken}` }
        })
            .then((res) => {
                setCancellationReasons(prev => [...prev, res.data]);
                setNewReason('');
                toast.success("Reason added");
            })
            .catch(err => {
                console.error("Error adding reason:", err);
                toast.error("Failed to add reason");
            });
    };

    const handleDeleteReason = async (id) => {
        setIsLoading(true);
        try {
            await axios.delete(`${BASE_URL}/orders/api/cancellations/${epicxTenantId}/${id}`, {
                headers: { Authorization: `Bearer ${AuthorizationToken}` }
            });
            setCancellationReasons(prev => prev.filter(r => r._id !== id));
            toast.success("Reason deleted");
            setShowDeleteConfirm(null);
        } catch (err) {
            console.error("Error deleting reason:", err);
            toast.error("Failed to delete reason");
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveEditedReason = async (id) => {
        if (!editedReasonText.trim()) return;

        setIsLoading(true);
        try {
            const res = await axios.put(`${BASE_URL}/orders/api/cancellations/${epicxTenantId}/${id}`, { 
                reason: editedReasonText,
                active: editedActiveStatus
            }, {
                headers: { Authorization: `Bearer ${AuthorizationToken}` }
            });
            setCancellationReasons(prev =>
                prev.map(r => (r._id === id ? res.data : r))
            );
            setEditReasonId(null);
            toast.success("Reason updated");
        } catch (err) {
            console.error("Error updating reason:", err);
            toast.error("Failed to update reason");
        } finally {
            setIsLoading(false);
        }
    };

    const filteredReasons = cancellationReasons.filter((reason) =>
        reason.reason.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <>
            <Sidebar />
            <div className="vendors-page settings-page">
                <header className="vendors-hero">
                    <div>
                        <p className="vendors-kicker">Setup</p>
                        <h1>Settings</h1>
                        <p>Store times, delivery windows, and cancellation reasons.</p>
                    </div>
                </header>
                <div className="vendors-card settings-card">
                    <div className="vendors-card-body">
                                    <Tabs defaultActiveKey="general" id="settings-tabs" className="mb-3 custom-tabs">
                                        <Tab eventKey="general" title="General Setting">
                                            <div className="p-3">
                                                <h5>General Settings</h5>
                                                <p>Content for general settings goes here.</p>
                                            </div>
                                        </Tab>

                                        <Tab eventKey="additional-settings" title="Additional Settings">
                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="productVendorOrderAcceptTime">Product Vendor Order Accept Time</label>
                                                    <div className="d-flex">
                                                        <input 
                                                            type="number" 
                                                            className="form-control bg-light me-2"
                                                            value={timeSettings.productVendorOrderAcceptTime.value}
                                                            onChange={(e) => handleTimeChange('productVendorOrderAcceptTime', 'value', e.target.value)} 
                                                        />
                                                        <select 
                                                            className="form-control bg-light"
                                                            value={timeSettings.productVendorOrderAcceptTime.unit}
                                                            onChange={(e) => handleTimeChange('productVendorOrderAcceptTime', 'unit', e.target.value)}
                                                        >
                                                            <option value="minutes">Minutes</option>
                                                            <option value="hours">Hours</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="deliveryStartTime">Delivery Start Time</label>
                                                    <input 
                                                        type="time" 
                                                        className="form-control bg-light"
                                                        value={timeSettings.deliveryStartTime}
                                                        onChange={(e) => setTimeSettings(prev => ({ ...prev, deliveryStartTime: e.target.value }))} 
                                                    />
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="deliveryEndTime">Delivery End Time</label>
                                                    <input 
                                                        type="time" 
                                                        className="form-control bg-light"
                                                        value={timeSettings.deliveryEndTime}
                                                        onChange={(e) => setTimeSettings(prev => ({ ...prev, deliveryEndTime: e.target.value }))} 
                                                    />
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="cutoffTime">Cut-off Time</label>
                                                    <div className="d-flex">
                                                        <input 
                                                            type="number" 
                                                            className="form-control bg-light me-2"
                                                            value={timeSettings.cutoffTime.value}
                                                            onChange={(e) => handleTimeChange('cutoffTime', 'value', e.target.value)} 
                                                        />
                                                        <select 
                                                            className="form-control bg-light"
                                                            value={timeSettings.cutoffTime.unit}
                                                            onChange={(e) => handleTimeChange('cutoffTime', 'unit', e.target.value)}
                                                        >
                                                            <option value="minutes">Minutes</option>
                                                            <option value="hours">Hours</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="cartRemoveTime">Cart Remove Time</label>
                                                    <div className="d-flex">
                                                        <input 
                                                            type="number" 
                                                            className="form-control bg-light me-2"
                                                            value={timeSettings.cartRemoveTime.value}
                                                            onChange={(e) => handleTimeChange('cartRemoveTime', 'value', e.target.value)} 
                                                        />
                                                        <select 
                                                            className="form-control bg-light"
                                                            value={timeSettings.cartRemoveTime.unit}
                                                            onChange={(e) => handleTimeChange('cartRemoveTime', 'unit', e.target.value)}
                                                        >
                                                            <option value="minutes">Minutes</option>
                                                            <option value="hours">Hours</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="reassignmentTimeout">Reassignment Timeout</label>
                                                    <div className="d-flex">
                                                        <input 
                                                            type="number" 
                                                            className="form-control bg-light me-2"
                                                            value={timeSettings.reassignmentTimeout.value}
                                                            onChange={(e) => handleTimeChange('reassignmentTimeout', 'value', e.target.value)} 
                                                        />
                                                        <select 
                                                            className="form-control bg-light"
                                                            value={timeSettings.reassignmentTimeout.unit}
                                                            onChange={(e) => handleTimeChange('reassignmentTimeout', 'unit', e.target.value)}
                                                            disabled
                                                        >
                                                            <option value="seconds">Seconds</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="row">
                                                <div className="col-md-4 mb-3">
                                                    <label htmlFor="recentorder">Recent Order</label>
                                                    <div className="d-flex">
                                                        <input 
                                                            type="number" 
                                                            className="form-control bg-light me-2"
                                                            value={timeSettings.recentorder.value}
                                                            onChange={(e) => handleTimeChange('recentorder', 'value', e.target.value)} 
                                                        />
                                                        <select 
                                                            className="form-control bg-light"
                                                            value={timeSettings.recentorder.unit}
                                                            onChange={(e) => handleTimeChange('recentorder', 'unit', e.target.value)}
                                                        >
                                                            <option value="minutes">Minutes</option>
                                                            <option value="hours">Hours</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="settings-actions">
                                                <Button className="settings-cancel" onClick={handleCancel}>Cancel</Button>
                                                <Button className="settings-save" onClick={handleSave}>Save</Button>
                                            </div>
                                        </Tab>

                                        <Tab eventKey="cancellation-reasons" title="Cancellation Reasons">
                                            <div className="p-3">
                                                <h5>Cancellation Reasons</h5>
                                                <div className="mb-3 d-flex">
                                                    <input
                                                        type="text"
                                                        className="form-control me-2"
                                                        placeholder="Enter a cancellation reason"
                                                        value={newReason}
                                                        onChange={(e) => setNewReason(e.target.value)}
                                                    />
                                                    <Button className="settings-save me-2" onClick={handleAddReason}>Add</Button>
                                                    <Button className="settings-view" onClick={() => setShowModal(true)}>View All</Button>
                                                </div>
                                            </div>
                                        </Tab>
                                    </Tabs>
                    </div>
                </div>
                <div className="vendors-footer"><Footer /></div>
            </div>

            <Modal
                show={showModal}
                onHide={() => setShowModal(false)}
                centered
                size="lg"
                dialogClassName="vendor-details-modal"
                aria-labelledby="cancellation-reasons-modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title id="cancellation-reasons-modal">
                        Cancellation Reasons
                    </Modal.Title>
                </Modal.Header>
                <Modal.Body className="p-4" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <Form.Group className="mb-3">
                        <div className="input-group">
                            <span className="input-group-text bg-white border-end-0">
                                <FaSearch className="text-muted" />
                            </span>
                            <Form.Control
                                type="text"
                                placeholder="Search reasons..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="border-start-0"
                                aria-label="Search cancellation reasons"
                            />
                        </div>
                    </Form.Group>

                    {filteredReasons.length === 0 ? (
                        <Alert variant="info" className="text-center">
                            No cancellation reasons found.
                        </Alert>
                    ) : (
                        <div className="reason-list">
                            {filteredReasons.map((reason) => (
                                <div key={reason._id} className="reason-item">
                                    {editReasonId === reason._id ? (
                                        <div className="reason-edit">
                                            <Form.Control
                                                type="text"
                                                value={editedReasonText}
                                                onChange={(e) => setEditedReasonText(e.target.value)}
                                                placeholder="Enter reason"
                                                aria-label={`Edit reason: ${reason.reason}`}
                                                disabled={isLoading}
                                            />
                                            <Form.Check
                                                type="switch"
                                                label="Active"
                                                checked={editedActiveStatus}
                                                onChange={(e) => setEditedActiveStatus(e.target.checked)}
                                                disabled={isLoading}
                                                aria-label={`Toggle active status for ${reason.reason}`}
                                            />
                                            <button type="button" className="reason-save" onClick={() => handleSaveEditedReason(reason._id)} disabled={isLoading || !editedReasonText.trim()}>
                                                <FaSave /> Save
                                            </button>
                                            <button type="button" className="reason-dismiss" onClick={() => setEditReasonId(null)} disabled={isLoading}>
                                                <FaTimes /> Cancel
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="reason-row">
                                            <div className="reason-copy">
                                                <span>{reason.reason}</span>
                                                <Badge bg={reason.active ? 'success' : 'secondary'}>
                                                    {reason.active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </div>
                                            <div className="reason-actions">
                                                <button
                                                    type="button"
                                                    className="reason-edit-btn"
                                                    onClick={() => {
                                                        setEditReasonId(reason._id);
                                                        setEditedReasonText(reason.reason);
                                                        setEditedActiveStatus(reason.active);
                                                        setShowDeleteConfirm(null);
                                                    }}
                                                    aria-label={`Edit ${reason.reason}`}
                                                >
                                                    <FaEdit /> Edit
                                                </button>
                                                <button
                                                    type="button"
                                                    className="reason-delete-btn"
                                                    onClick={() => setShowDeleteConfirm(reason._id)}
                                                    disabled={isLoading}
                                                    aria-label={`Delete ${reason.reason}`}
                                                >
                                                    <FaTrash /> Delete
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {showDeleteConfirm === reason._id && (
                                        <div className="reason-confirm">
                                            <p>Delete this reason? This cannot be undone.</p>
                                            <strong>{reason.reason}</strong>
                                            <div>
                                                <button type="button" className="reason-dismiss" onClick={() => setShowDeleteConfirm(null)} disabled={isLoading}>
                                                    Cancel
                                                </button>
                                                <button type="button" className="reason-confirm-delete" onClick={() => handleDeleteReason(reason._id)} disabled={isLoading}>
                                                    {isLoading ? 'Deleting...' : 'Yes, delete'}
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="vendors-modal-close" onClick={() => setShowModal(false)} disabled={isLoading}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>

            <ToastContainer />
        </>
    );
};

export default Settings;